// Delivery / Photos page — displays real service photos from the backend
import { useState, useEffect, useCallback } from 'react';
import Header from '@/components/layout/Header';
import api from '@/api/client';
import { useProperty } from '@/hooks/useProperty';
import { Calendar, Image, X, ChevronLeft, ChevronRight, MapPin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Convert relative photo URL to proxied URL with auth token
function photoUrl(url) {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  const token = localStorage.getItem('token');
  // /api/rooms/{room_id}/photos/{filename} → /api/photos/{room_id}/{filename}?token=...
  const match = url.match(/\/api\/rooms\/([^/]+)\/photos\/(.+)/);
  if (match) {
    const base = `${BACKEND_URL}/api/photos/${match[1]}/${match[2]}`;
    return token ? `${base}?token=${token}` : base;
  }
  return `${BACKEND_URL}${url}`;
}

export default function Delivery({ user }) {
  const { selectedProperty } = useProperty();
  const [loading, setLoading] = useState(true);
  const [photoData, setPhotoData] = useState({ photos: [], spaces: [] });
  const [sharedPhotoDates, setSharedPhotoDates] = useState(null); // null = not loaded, [] = none shared
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewingPhoto, setViewingPhoto] = useState(null);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [calOpen, setCalOpen] = useState(false);
  const token = localStorage.getItem('token');

  const loadPhotos = useCallback(async () => {
    if (!selectedProperty) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [photoResult, dashResult] = await Promise.allSettled([
        api.getLandscapePhotos(selectedProperty.id),
        api.getDashboardData(selectedProperty.id)
      ]);

      // Determine shared dates from dashboard data
      // - dashResult failed → null (can't determine, show all as fallback)
      // - dashResult OK, shared_photo_dates is array with dates → use those dates
      // - dashResult OK, shared_photo_dates is empty [] → manager hasn't shared any dates
      // - dashResult OK, shared_photo_dates is undefined → field not present, show all
      let shared = null; // null = unknown/failed, show all
      let dashSucceeded = false;
      if (dashResult.status === 'fulfilled') {
        dashSucceeded = true;
        const rawShared = dashResult.value?.shared_photo_dates;
        if (Array.isArray(rawShared)) {
          shared = rawShared; // could be [] (empty) or ['2026-03-30', ...]
        }
        // if rawShared is undefined/null, shared stays null → show all
      }
      setSharedPhotoDates(shared);

      if (photoResult.status === 'fulfilled') {
        const data = photoResult.value;
        const propId = selectedProperty.id;
        let filteredPhotos = (data.photos || []).filter(p => p.property_id === propId);

        if (shared !== null) {
          if (shared.length > 0) {
            // Filter to only manager-shared dates
            filteredPhotos = filteredPhotos.filter(p => {
              const photoDate = p.uploaded_at?.split('T')[0];
              return shared.includes(photoDate);
            });
          } else {
            // shared_photo_dates is empty [] — manager hasn't shared any dates
            filteredPhotos = [];
          }
        }
        // shared === null → dashboard failed or field missing, show all property photos

        const filteredSpaces = (data.spaces || []).filter(s => s.property_id === propId);
        setPhotoData({ photos: filteredPhotos, spaces: filteredSpaces });
      }
    } catch (err) {
      console.error('Failed to load photos:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedProperty]);

  useEffect(() => {
    // Reset state when property changes
    setPhotoData({ photos: [], spaces: [] });
    setSelectedDate(null);
    setViewingPhoto(null);
    loadPhotos();
  }, [loadPhotos]);

  // Group photos by date, then by space
  const photos = photoData.photos || [];
  const spaces = photoData.spaces || [];

  const photosByDate = {};
  photos.forEach(p => {
    const date = p.uploaded_at?.split('T')[0] || 'unknown';
    if (!photosByDate[date]) photosByDate[date] = {};
    const space = p.space_name?.trim() || 'Other';
    if (!photosByDate[date][space]) photosByDate[date][space] = [];
    photosByDate[date][space].push(p);
  });

  const sortedDates = Object.keys(photosByDate).sort((a, b) => b.localeCompare(a));
  const sharedDates = sortedDates;

  // Calendar data
  const today = new Date();
  const [calMonth, setCalMonth] = useState(today.getMonth());
  const [calYear, setCalYear] = useState(today.getFullYear());

  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const firstDayOfWeek = new Date(calYear, calMonth, 1).getDay();
  const monthName = new Date(calYear, calMonth).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const calDays = [];
  for (let i = 0; i < firstDayOfWeek; i++) calDays.push(null);
  for (let d = 1; d <= daysInMonth; d++) calDays.push(d);

  const hasPhotosOnDay = (day) => {
    if (!day) return false;
    const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return sharedDates.includes(dateStr);
  };

  const handleDayClick = (day) => {
    if (!day) return;
    const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    if (sharedDates.includes(dateStr)) {
      setSelectedDate(selectedDate === dateStr ? null : dateStr);
    }
  };

  const formatDateLabel = (dateStr) => {
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  // Which dates to show in the gallery
  const visibleDates = selectedDate ? [selectedDate] : sortedDates;

  // Flatten photos for a space on a given date (for the viewer)
  const getSpacePhotos = (date, spaceName) => {
    return photosByDate[date]?.[spaceName] || [];
  };

  const totalPhotos = photos.length;
  const totalSpaces = spaces.length;
  const totalDays = sharedDates.length;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-light">
        <Header title="Service Delivery" property={selectedProperty} />
        <div className="p-4 sm:p-6 md:p-8 max-w-4xl mx-auto">
          {[1, 2, 3].map(i => (
            <div key={i} className="sg-card p-4 mb-3 animate-pulse">
              <div className="h-3 bg-slate/20 rounded w-32 mb-3"></div>
              <div className="h-20 bg-slate/20 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-light pb-24" data-testid="delivery-page">
      <Header title="Service Delivery" property={selectedProperty} />
      
      <div className="p-4 sm:p-6 md:p-8 max-w-4xl mx-auto">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-4" data-testid="photo-stats">
          <div className="sg-card p-3 text-center">
            <p className="font-cormorant text-xl font-semibold text-navy">{totalSpaces}</p>
            <p className="font-dm text-[9px] text-slate">Spaces</p>
          </div>
          <div className="sg-card p-3 text-center">
            <p className="font-cormorant text-xl font-semibold text-navy">{totalPhotos}</p>
            <p className="font-dm text-[9px] text-slate">Photos</p>
          </div>
          <div className="sg-card p-3 text-center">
            <p className="font-cormorant text-xl font-semibold text-navy">{totalDays}</p>
            <p className="font-dm text-[9px] text-slate">{totalDays === 1 ? 'Day' : 'Days'}</p>
          </div>
        </div>

        {/* Calendar — collapsible dropdown */}
        <div className="sg-card mb-4 overflow-hidden" data-testid="photo-calendar">
          <button 
            onClick={() => setCalOpen(!calOpen)}
            className="w-full flex items-center justify-between px-4 py-3"
            data-testid="calendar-toggle"
          >
            <div className="flex items-center gap-2">
              <Calendar size={14} className="text-slate" />
              <p className="font-dm text-xs font-bold text-navy">{monthName}</p>
              {selectedDate && (
                <span className="text-[9px] font-dm px-2 py-0.5 rounded-full bg-green-light text-green font-medium">
                  {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
              )}
            </div>
            <ChevronRight size={14} className={`text-slate transition-transform duration-200 ${calOpen ? 'rotate-90' : ''}`} />
          </button>

          {calOpen && (
            <div className="px-4 pb-4 border-t border-black/[0.06] pt-3">
              <div className="flex items-center justify-between mb-3">
                <button onClick={() => { if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); } else setCalMonth(m => m - 1); }}
                  className="w-7 h-7 rounded-full bg-slate-light flex items-center justify-center">
                  <ChevronLeft size={14} className="text-slate" />
                </button>
                <p className="font-dm text-xs font-bold text-navy">{monthName}</p>
                <button onClick={() => { if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); } else setCalMonth(m => m + 1); }}
                  className="w-7 h-7 rounded-full bg-slate-light flex items-center justify-center">
                  <ChevronRight size={14} className="text-slate" />
                </button>
              </div>

              <div className="grid grid-cols-7 gap-1 text-center">
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
                  <p key={d} className="font-dm text-[9px] text-slate py-1">{d}</p>
                ))}
                {calDays.map((day, i) => {
                  const hasPhotos = hasPhotosOnDay(day);
                  const dateStr = day ? `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}` : '';
                  const isSelected = dateStr === selectedDate;
                  return (
                    <button
                      key={i}
                      onClick={() => handleDayClick(day)}
                      disabled={!day}
                      className={`relative w-full aspect-square flex items-center justify-center rounded-full text-[11px] font-dm transition-colors
                        ${!day ? '' : hasPhotos ? 'cursor-pointer font-bold' : 'text-slate/40'}
                        ${isSelected ? 'bg-navy text-white' : hasPhotos ? 'text-navy hover:bg-slate-light' : ''}`}
                    >
                      {day}
                      {hasPhotos && !isSelected && (
                        <span className="absolute bottom-0 w-2 h-2 rounded-full bg-green"></span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Photo Gallery */}
        {visibleDates.length > 0 ? (
          visibleDates.map(date => (
            <div key={date} className="mb-4">
              <p className="sg-label mb-2">
                <Calendar size={12} className="inline mr-1" />
                {formatDateLabel(date)}
              </p>

              {Object.entries(photosByDate[date] || {}).map(([spaceName, spacePhotos]) => (
                <div key={spaceName} className="sg-card mb-3 overflow-hidden">
                  <div className="px-4 py-2.5 bg-slate-light/50 border-b border-black/[0.06] flex items-center gap-2">
                    <MapPin size={12} className="text-slate" />
                    <p className="font-dm text-[11px] font-bold text-navy">{spaceName}</p>
                    <span className="font-dm text-[9px] text-slate ml-auto">{spacePhotos.length} photo{spacePhotos.length !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-0.5 p-0.5">
                    {spacePhotos.map((photo, idx) => (
                      <button
                        key={idx}
                        onClick={() => { setViewingPhoto({ date, spaceName, photos: spacePhotos }); setViewerIndex(idx); }}
                        className="aspect-square overflow-hidden bg-slate-light hover:opacity-80 transition-opacity"
                        data-testid={`photo-thumb-${date}-${idx}`}
                      >
                        <img
                          src={photoUrl(photo.url)}
                          alt={`${spaceName} photo ${idx + 1}`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          headers={token ? { Authorization: `Bearer ${token}` } : undefined}
                          onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.classList.add('flex', 'items-center', 'justify-center'); e.target.parentElement.innerHTML = '<span class="text-slate/30"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg></span>'; }}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))
        ) : (
          <div className="sg-card p-8 text-center">
            <Image size={32} className="text-slate/30 mx-auto mb-3" />
            {Array.isArray(sharedPhotoDates) && sharedPhotoDates.length === 0 ? (
              <>
                <p className="font-dm text-xs font-medium text-navy mb-1" data-testid="no-shared-photos">No shared photos yet</p>
                <p className="font-dm text-[10px] text-slate">Your property manager will share service photos after your next visit</p>
              </>
            ) : (
              <>
                <p className="font-dm text-xs font-medium text-navy mb-1" data-testid="no-photos">No photos yet</p>
                <p className="font-dm text-[10px] text-slate">Service photos will appear here after your next visit</p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Photo Viewer Modal */}
      {viewingPhoto && (
        <div className="fixed inset-0 bg-black/95 z-[99999] flex flex-col" data-testid="photo-viewer-modal">
          <div className="flex items-center justify-between p-4" style={{ paddingTop: 'max(16px, env(safe-area-inset-top))' }}>
            <div>
              <p className="text-white font-dm text-sm font-bold">{viewingPhoto.spaceName}</p>
              <p className="text-white/60 font-dm text-xs">{formatDateLabel(viewingPhoto.date)}</p>
            </div>
            <button
              onClick={() => { setViewingPhoto(null); setViewerIndex(0); }}
              className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white"
              data-testid="close-viewer-btn"
            >
              <X size={20} />
            </button>
          </div>

          <div className="flex-1 flex items-center justify-center p-4 relative">
            {viewingPhoto.photos.length > 1 && viewerIndex > 0 && (
              <button onClick={() => setViewerIndex(i => i - 1)}
                className="absolute left-2 z-10 w-10 h-10 rounded-full bg-black/40 flex items-center justify-center text-white">
                <ChevronLeft size={20} />
              </button>
            )}

            <img
              src={photoUrl(viewingPhoto.photos[viewerIndex]?.url)}
              alt={`${viewingPhoto.spaceName} ${viewerIndex + 1}`}
              className="max-w-full max-h-[70vh] object-contain rounded-xl"
            />

            {viewingPhoto.photos.length > 1 && viewerIndex < viewingPhoto.photos.length - 1 && (
              <button onClick={() => setViewerIndex(i => i + 1)}
                className="absolute right-2 z-10 w-10 h-10 rounded-full bg-black/40 flex items-center justify-center text-white">
                <ChevronRight size={20} />
              </button>
            )}
          </div>

          <div className="p-4 text-center">
            <p className="text-white/40 font-dm text-[10px]">
              {viewerIndex + 1} of {viewingPhoto.photos.length}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
